import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { Button, LocalVehicle } from '..';
import states from '../../assets/states.json';
import _ from 'lodash';
import './VehicleModal.scss';
import { useDispatch } from 'react-redux';
import { fetchTripError } from '../../store/actions';
import { TranslationUtils } from '../../utils';
import { DetailedError } from '../../types/errors.type';

export enum VehicleModalOptions {
    ADD = 'add',
    EDIT = 'edit',
    REMOVE = 'remove',
    NO_VEHICLES = 'noVehicles',
    DEFAULT = '',
}

export enum StateVehicleType {
    OTHER = 'other',
}

export interface VehicleModalProps {
    show: boolean;
    isSubmitDisabled: boolean;
    isLoadingSubmit: boolean;
    localVehicle: LocalVehicle;
    option: VehicleModalOptions;
    onClose: () => void;
    onGotoRemove: () => void;
    onPropertyChange: (e: any) => void;
    onSubmit: () => void;
}

const VehicleModal: React.FC<VehicleModalProps> = (props) => {
    const {
        localVehicle: vehicle,
        show,
        option,
        isSubmitDisabled,
        onClose,
        onGotoRemove,
        onPropertyChange,
        onSubmit,
        isLoadingSubmit,
    } = props;
    const countryGroups = _.groupBy(states, 'country');
    const dispatch = useDispatch();
    const [otherStateClassName, setOtherStateClassName] = useState('hidden');

    useEffect(() => {
        const className = vehicle.stateCode === StateVehicleType.OTHER ? '' : 'hidden';
        setOtherStateClassName(className);
    }, [vehicle.stateCode]);

    const renderInput = (id: string, name: string, placeHolder: string, value: string, disabled: boolean) => {
        return (
            <input
                id={id}
                className="form-control "
                name={name}
                placeholder={TranslationUtils.get(placeHolder)}
                type="text"
                value={value}
                onChange={onPropertyChange}
                disabled={disabled}
            />
        );
    };

    const renderFields = (disabled: boolean = false) => {
        return (
            <React.Fragment>
                <div className="row vehicle-row-control">
                    <div className="col-12 col-sm-12 col-md-6 plate-padding">
                        {renderInput('vehicle-plate', 'plate', 'license_plate', vehicle.plate, disabled)}
                    </div>
                    <div className="col-12 col-sm-12 col-md-6 state-padding">
                        <select
                            id="vehicle-country"
                            className="form-control"
                            onChange={onPropertyChange}
                            value={vehicle.stateCode}
                            name="stateCode"
                            disabled={disabled}
                        >
                            <option value="0" disabled={true}>
                                {TranslationUtils.get('state_province')}
                            </option>
                            {Object.keys(countryGroups).map((country) => {
                                return (
                                    <optgroup key={country} label={country}>
                                        {countryGroups[country].map((state) => {
                                            return (
                                                <option key={state.stateID} value={state.state}>
                                                    {state.stateName}
                                                </option>
                                            );
                                        })}
                                    </optgroup>
                                );
                            })}
                            <optgroup key="other" label={TranslationUtils.get('other')}>
                                <option value="other">{TranslationUtils.get('other')}</option>
                            </optgroup>
                        </select>
                    </div>
                </div>
                <div className={`row vehicle-second-row-control ${otherStateClassName}`}>
                    <div className="col-12 col-sm-12 col-md-6 offset-md-6 state-padding other-state">
                        {renderInput('vehicle-other-country', 'otherState', 'location', vehicle.otherState, disabled)}
                    </div>
                </div>
            </React.Fragment>
        );
    };

    const renderButtons = (submitId: string, cancelId: string, submitDisplayText: string) => {
        const submitButton = (
            <div className="col-12 col-sm-12 col-md-6 submit-action-button">
                <Button
                    id={submitId}
                    className="vehicle-large-button"
                    variant="primary"
                    label={TranslationUtils.get(submitDisplayText)}
                    onClick={onSubmit}
                    disabled={isSubmitDisabled}
                    isLoading={isLoadingSubmit}
                />
            </div>
        );
        const cancelButton = (
            <div className="col-12 col-sm-12 col-md-6 cancel-action-button">
                <Button
                    id={cancelId}
                    className="vehicle-large-button"
                    variant="default"
                    label={TranslationUtils.get(`${option === VehicleModalOptions.EDIT ? 'remove_vehicle_button' : 'cancel'}`)}
                    onClick={option === VehicleModalOptions.EDIT ? onGotoRemove : onClose}
                />
            </div>
        );

        return (
            <React.Fragment>
                <div className="d-md-none">
                    <div className="row mb-2 vehicle-row-control">
                        {submitButton} {cancelButton}
                    </div>
                </div>
                <div className="d-none d-md-block">
                    <div className="row mb-2 vehicle-row-control">
                        {cancelButton} {submitButton}
                    </div>
                </div>
            </React.Fragment>
        );
    };

    const renderAdd = () => {
        return (
            <React.Fragment>
                <Modal.Header closeButton />
                <div id="add-vehicles-modal" className="invite-vehicle-modal">
                    <div className="invite-vehicle-modal-text">
                        <h4 id="add-vehicle-title">{TranslationUtils.get('add_vehicle_title')}</h4>
                        <br />
                    </div>
                    {renderFields()}
                    {renderButtons('button-submit-add-vehicle', 'button-cancel-add-vehicle', 'confirm')}
                </div>
            </React.Fragment>
        );
    };

    const renderEdit = () => {
        return (
            <React.Fragment>
                <Modal.Header closeButton />
                <div className="invite-vehicle-modal">
                    <div className="invite-vehicle-modal-text">
                        <h4 id="edit-vehicle-title">{TranslationUtils.get('edit_vehicle_title')}</h4>
                        <br />
                    </div>
                    {renderFields()}
                    {renderButtons('button-submit-edit-vehicle', 'button-cancel-edit-vehicle', 'edit_a_vehicle_button')}
                </div>
            </React.Fragment>
        );
    };

    const renderRemove = () => {
        return (
            <React.Fragment>
                <Modal.Header closeButton />
                <div className="invite-vehicle-modal">
                    <div className="invite-vehicle-modal-text">
                        <h4 id="remove-vehicle-title">{TranslationUtils.get('remove_vehicle_title')}</h4>
                        <br />
                    </div>
                    {renderFields(true)}
                    {renderButtons('button-submit-remove-vehicle', 'button-cancel-remove-vehicle', 'confirm')}
                </div>
            </React.Fragment>
        );
    };

    const renderNoVehicles = () => {
        return (
            <React.Fragment>
                <Modal.Header closeButton />
                <div id="no-vehicles-modal" className="invite-vehicle-modal">
                    <div className="invite-vehicle-modal-text">
                        <h4 id="no-vehicle-title">{TranslationUtils.get('are_you_sure')}</h4>
                        <p>{TranslationUtils.get('my_group_has_no_vehicles_confirm_text')}</p>
                    </div>
                    {renderButtons(
                        'button-submit-vehicle-opt-out',
                        'button-cancel-vehicle-opt-out',
                        'my_group_has_no_vehicles_confirm_button'
                    )}
                </div>
            </React.Fragment>
        );
    };

    const renderBody = (renderOption: VehicleModalOptions) => {
        switch (renderOption) {
            case VehicleModalOptions.ADD:
                return renderAdd();

            case VehicleModalOptions.EDIT:
                return renderEdit();

            case VehicleModalOptions.REMOVE:
                return renderRemove();

            case VehicleModalOptions.NO_VEHICLES:
                return renderNoVehicles();

            default:
                dispatch(fetchTripError(new Error('Vehicle option not implemeted') as DetailedError));
        }
    };

    return (
        <Modal show={show} onHide={onClose}>
            {renderBody(option)}
        </Modal>
    );
};

export default VehicleModal;
